import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import LeaderTileStrip from './LeaderTileStrip';
import StbAccordion from '../security-toolbox-ui/StbAccordion';

import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../types/dashboard';
import { TileRuntimeContext } from '../../support/tileRuntimeContext';

/**
 * A self-contained, config-driven Leader dashboard region. It fetches the `dashboard_placement` for a
 * `(component_key, region_key)` and its ordered `dashboard_tile_link` rows, then renders each linked
 * CustomTile through `LeaderTileStrip`, substituting the passed `runtimeContext` tokens (e.g.
 * `{managerEmail}`) into each tile's query params. The layout (grid columns/gap, container height/
 * padding, accordion label + defaultOpen) is driven by the placement's `layout.options`.
 *
 * This is the shared plumbing behind the Salesforce/Campaigns/Enablement team dashboards: the tile
 * CONTENT (which endpoints, transforms, chart settings) lives in the DB, keyed on `component_key`, so
 * these pages carry no hard-coded chart config. The drill-in per-member tables remain dedicated
 * components alongside this region (they need per-request detail a single rollup tile cannot express).
 */

/** Default definite height (px) for a placed chart tile when the DB link carries no explicit height.
 *  Sized so the LeaderTile header + a ~300px chart body fit — the analytics dashboard chart height. */
export const DEFAULT_TILE_HEIGHT = 340;
/** Grid gutter (px) — matches the shared `DASH.gridGap` used by the inline-card tabs. */
export const DEFAULT_GRID_GAP = 16;
/**
 * Vertical space (px) the LeaderTile card chrome consumes on top of the chart/metric BODY: the divided
 * header (~44px) plus the body's top+bottom padding (~24px). A DB link's `minHeight` was authored as the
 * intended CONTENT height (e.g. 120 for a KPI strip, 280 for a chart) BEFORE the card header/padding
 * existed, so we add this allowance to the configured height. Without it the header eats into the body
 * and short tiles (KPI/metric strips) clip their value+label under the body's `overflow:hidden`.
 */
export const CARD_CHROME_ALLOWANCE = 64;

/**
 * Optional per-region layout override applied on the FRONTEND, on top of the DB placement config. The
 * tile CONTENT (endpoints/transforms/settings) stays DB-driven; this only re-flows which tiles share a
 * row so a page can pin a specific dashboard layout (e.g. "these three charts in one row") without
 * waiting on a backend reseed. Keep these in sync with the seed (`_leader_rollup_tiles_data.py` /
 * `_leader_tiles_data.py`) so the config source of truth matches what renders.
 */
export interface PlacementLayoutOverride {
  /** Grid column count for the region (overrides `placement.layout.options.grid.columns`). */
  columns?: number;
  /** Explicit ordered tiles: defines render order + colSpan. Tiles not listed are appended as-is (a
   *  safety net against tile-name drift), so an override never makes a configured tile disappear. */
  tiles?: Array<{ name: string; colSpan?: number }>;
}

interface Props {
  componentKey: string;
  regionKey?: string;
  runtimeContext: TileRuntimeContext;
  /** Accordion label when the placement has none; also the empty-state subject. */
  fallbackLabel?: string;
  fallbackColumns?: number;
  fallbackHeight?: number;
  /** FE layout override (row re-flow); see {@link PlacementLayoutOverride}. */
  layoutOverride?: PlacementLayoutOverride;
  enableDebugLogging?: boolean;
  /** Extra content rendered inside the accordion, below the tile grid. */
  children?: React.ReactNode;
}

function getPlacementLayoutOptions(placement: any): any {
  return placement?.layout?.options ?? placement?.placement_layout?.options ?? placement?.options ?? {};
}

function getGridColumns(placement: any, fallback: number) {
  return Number(getPlacementLayoutOptions(placement)?.grid?.columns ?? fallback);
}

function getContainerHeight(placement: any, fallback: number) {
  return Number(getPlacementLayoutOptions(placement)?.container?.height ?? fallback);
}

function getContainerPadding(placement: any, fallback: number) {
  return Number(getPlacementLayoutOptions(placement)?.container?.padding ?? fallback);
}

function getGridGap(placement: any, fallback: number) {
  const opts = getPlacementLayoutOptions(placement);
  return Number(opts?.grid?.gap ?? opts?.container?.gap ?? fallback);
}

function getAccordionDefaultOpen(placement: any, fallback: boolean) {
  const opts = getPlacementLayoutOptions(placement);
  const v = opts?.accordion?.defaultOpen;
  return typeof v === 'boolean' ? v : fallback;
}

/** Resolve the definite per-tile card height from a link's configured content `minHeight` plus the card
 *  chrome allowance (see {@link CARD_CHROME_ALLOWANCE}). Falls back to {@link DEFAULT_TILE_HEIGHT}. */
function resolveTileHeight(link: DashboardTileLink): number {
  const configuredHeight = Number((link.options as any)?.minHeight);
  return Number.isFinite(configuredHeight) && configuredHeight > 0
    ? configuredHeight + CARD_CHROME_ALLOWANCE
    : DEFAULT_TILE_HEIGHT;
}

/** Build the ordered `{ link, colSpan }` render list, honoring an optional FE layout override. In
 *  override mode the listed tiles define order + colSpan and any un-listed DB tiles are appended as-is. */
export function buildOrderedTileList(
  tileLinks: DashboardTileLink[],
  override?: PlacementLayoutOverride,
): Array<{ link: DashboardTileLink; colSpan: number }> {
  const sorted = tileLinks
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
    .filter((l) => !!l?.tile?.name);

  if (!override?.tiles?.length) {
    return sorted.map((link) => ({ link, colSpan: Number((link.options as any)?.colSpan ?? 1) }));
  }

  const linkByName = new Map<string, DashboardTileLink>();
  sorted.forEach((l) => {
    const n = l.tile?.name as string;
    if (!linkByName.has(n)) linkByName.set(n, l);
  });

  const used = new Set<string>();
  const ordered: Array<{ link: DashboardTileLink; colSpan: number }> = [];
  for (const t of override.tiles) {
    const link = linkByName.get(t.name);
    if (!link) continue;
    used.add(t.name);
    ordered.push({ link, colSpan: Number(t.colSpan ?? (link.options as any)?.colSpan ?? 1) });
  }
  // Safety net: append any configured tile the override didn't name, so a name mismatch can never drop it.
  for (const link of sorted) {
    const n = link.tile?.name as string;
    if (!used.has(n)) ordered.push({ link, colSpan: Number((link.options as any)?.colSpan ?? 1) });
  }
  return ordered;
}

function renderPlacementGrid({
  tileLinks,
  columns,
  gap,
  runtimeContext,
  enableDebugLogging,
  gridKeyPrefix,
  layoutOverride,
}: {
  tileLinks: DashboardTileLink[];
  columns: number;
  gap: number;
  runtimeContext: TileRuntimeContext;
  enableDebugLogging: boolean;
  gridKeyPrefix: string;
  layoutOverride?: PlacementLayoutOverride;
}) {
  if (!Array.isArray(tileLinks) || tileLinks.length === 0) return null;

  const effectiveColumns = Math.max(1, layoutOverride?.columns ?? columns);
  const ordered = buildOrderedTileList(tileLinks, layoutOverride);

  return (
    <div
      style={{
        display: 'grid',
        gap,
        minWidth: 0,
        gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
        alignItems: 'stretch',
      }}
    >
      {ordered.map(({ link, colSpan }) => {
        const tileName = link?.tile?.name;
        if (!tileName) return null;

        // DEFINITE per-tile height (not `minHeight` + `height:100%`). Nothing above this grid has a
        // definite pixel height — the region is `minHeight` so it can grow — so leaving the tile at
        // `height:100%` collapsed the chart to whatever `minHeight` the DB link carried (often 0),
        // which is why the charts looked cramped/mis-sized. Pinning each tile to a real height gives
        // the chart body (nivo donuts especially) a stable box and yields a uniform card grid; the
        // region still grows to fit the rows.
        const tileHeight = resolveTileHeight(link);

        return (
          <div
            key={`${gridKeyPrefix}-tile-${link.id}`}
            className="min-w-0 flex flex-col"
            style={{
              height: tileHeight,
              minWidth: 0,
              gridColumn: `span ${Math.max(1, Math.min(effectiveColumns, colSpan))}`,
            }}
          >
            <LeaderTileStrip
              tileNames={[tileName]}
              runtimeContext={runtimeContext}
              className="h-full min-w-0"
              enableDebugLogging={enableDebugLogging}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function LeaderConfigDashboard({
  componentKey,
  regionKey = 'dashboard',
  runtimeContext,
  fallbackLabel = 'Dashboard',
  fallbackColumns = 2,
  fallbackHeight = 400,
  layoutOverride,
  enableDebugLogging = false,
  children,
}: Props) {
  const {
    data: placements,
    isLoading: isLoadingPlacements,
    error: placementsError,
  } = useFetchDashboardPlacements(
    { component_key: componentKey, region_key: regionKey, enabled: true },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const placement: DashboardPlacement | null = useMemo(() => {
    const arr = Array.isArray(placements) ? placements : [];
    // The backend region_key/component_key filters are case-insensitive CONTAINS, not exact — so a
    // query for region "dashboard" also returns "dashboard_attainment"/"dashboard_progression"/etc.
    // Pick the EXACT match (never a bare arr[0]) so two regions on one page can't collide onto the
    // same placement and render twice.
    return (
      arr.find((p) => p?.region_key === regionKey && p?.component_key === componentKey) ?? null
    );
  }, [placements, regionKey, componentKey]);

  const {
    data: tileLinks,
    isLoading: isLoadingLinks,
    error: linksError,
  } = useFetchDashboardTileLinks(
    placement?.id ? { placement: placement.id, enabled: true, order_by: 'order' } : undefined,
    { enabled: !!placement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const orderedLinks = useMemo(
    () => (Array.isArray(tileLinks) ? tileLinks : []) as DashboardTileLink[],
    [tileLinks],
  );
  const hasTiles = !!placement && orderedLinks.some((l) => !!l?.tile?.name);

  const [isOpen, setIsOpen] = useState(true);
  const defaultOpen = getAccordionDefaultOpen(placement, true);
  // Keep the accordion honoring the placement's configured defaultOpen until the user toggles it.
  const open = isOpen && defaultOpen === false ? false : isOpen;

  const columns = getGridColumns(placement, fallbackColumns);
  const height = getContainerHeight(placement, fallbackHeight);
  const padding = getContainerPadding(placement, 12);
  const gap = getGridGap(placement, DEFAULT_GRID_GAP);

  if (placementsError) {
    return <div style={{ padding: 12, color: 'red' }}>{(placementsError as Error).message}</div>;
  }
  if (linksError) {
    return <div style={{ padding: 12, color: 'red' }}>{(linksError as Error).message}</div>;
  }

  return (
    <StbAccordion
      variant="minimal"
      label={placement?.label || fallbackLabel}
      isOpen={open}
      toggleIsOpen={setIsOpen}
    >
      <div
        className="flex flex-col"
        style={{
          // minHeight (not a fixed height) so the region grows to fit its content. A fixed height
          // clipped tall grids (KPIs + multi-row charts + a datagrid) and let them overrun the
          // accordion onto the next section; each tile now carries its own definite height.
          minHeight: height,
          padding,
          gap,
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        {isLoadingPlacements || isLoadingLinks ? (
          <HbrSpinner>Loading dashboard layout from the Toolbox database…</HbrSpinner>
        ) : !hasTiles ? (
          <div className="hbr-css__text-secondary-sm">
            No dashboard tiles configured for {componentKey}.
          </div>
        ) : (
          <div style={{ minHeight: 0, minWidth: 0, padding: 0, overflow: 'visible' }}>
            {renderPlacementGrid({
              tileLinks: orderedLinks,
              columns,
              gap,
              runtimeContext,
              enableDebugLogging,
              gridKeyPrefix: `${componentKey}-${regionKey}`,
              layoutOverride,
            })}
          </div>
        )}
        {children}
      </div>
    </StbAccordion>
  );
}
